import { gql } from '@apollo/client';
import { Box, Grid, Typography } from '@mui/material';
import {
  SentimentColorMap,
  SentimentLabelMap,
} from 'features/socialListeningCommunity/constants';
import {
  Sentiment,
  SimpleDateRangeFilterInput,
  useGetStatsForSlCommunityStatsQuery,
} from 'graphql/generated';
import { useCallback, useMemo } from 'react';
import { theme } from 'styles/theme';
import { formatBigNumber } from 'utils/number';

// eslint-disable-next-line
gql`
  query GetStatsForSLCommunityStats(
    $input: GetCreatorProfileCommunityStatsArgs!
  ) {
    getCreatorProfileCommunityStats(input: $input) {
      percentageChange
      postCount
      sentiment
      totalEngagement
      totalViews
    }
  }
`;

interface SocialListeningCommunityStatsProps {
  community: string;
  brandId: string;
  dateRange: SimpleDateRangeFilterInput;
}

export const SocialListeningCommunityStats = ({
  community,
  brandId,
  dateRange,
}: SocialListeningCommunityStatsProps) => {
  const { data: communityStatsData } = useGetStatsForSlCommunityStatsQuery({
    variables: {
      input: {
        filters: {
          brandId,
          community,
          dateRange,
        },
      },
    },
  });
  const isNegativePercentChange = useMemo(() => {
    return communityStatsData?.getCreatorProfileCommunityStats?.percentageChange
      ? communityStatsData?.getCreatorProfileCommunityStats?.percentageChange <
          0
      : false;
  }, [communityStatsData]);

  const summaryData = useMemo(() => {
    const communityStats = communityStatsData?.getCreatorProfileCommunityStats;
    return [
      {
        label: 'Total post count',
        value: formatBigNumber(communityStats?.postCount ?? 0),
      },
      {
        label: 'Total view count',
        value: formatBigNumber(communityStats?.totalViews ?? 0),
      },
      {
        label: 'Total engagement',
        value: formatBigNumber(communityStats?.totalEngagement ?? 0),
      },
      {
        label: 'Percentage change',
        value: `${
          communityStats?.percentageChange === 0
            ? ''
            : isNegativePercentChange
            ? '↓ '
            : '↑ '
        }${
          communityStats?.percentageChange
            ? communityStats?.percentageChange.toFixed(2).replace('-', '')
            : 0
        }%`,
        sx: {
          whiteSpace: 'nowrap',
          '.summary-value': {
            color:
              theme.colors?.utility[
                communityStats?.percentageChange === 0
                  ? '900'
                  : isNegativePercentChange
                  ? 'pink-3'
                  : 'green-3'
              ],
          },
        },
      },
      {
        label: 'Overall Sentiment',
        value:
          SentimentLabelMap[communityStats?.sentiment ?? Sentiment.Positive],
        sx: {
          whiteSpace: 'nowrap',
          '.summary-value': {
            color:
              SentimentColorMap[
                communityStats?.sentiment ?? Sentiment.Positive
              ],
          },
        },
      },
    ];
  }, [
    isNegativePercentChange,
    communityStatsData?.getCreatorProfileCommunityStats,
  ]);

  const renderSummary = useCallback((summary, sx) => {
    return (
      <Box
        flexDirection="column"
        display="flex"
        alignItems="flex-start"
        gap={3}
        pl={4}
        sx={sx}
      >
        <Typography variant="headline-sm">{summary.label}</Typography>
        <Typography
          fontSize={theme.spacing(8)}
          variant="headline-xl"
          color={theme.colors?.utility[900]}
          fontWeight={500}
          letterSpacing="-1.28px"
          className="summary-value"
        >
          {summary.value}
        </Typography>
      </Box>
    );
  }, []);

  return (
    <Grid
      container
      spacing={2}
      sx={{
        backgroundColor: theme.colors?.primary.parchment,
        borderRadius: 2,
        py: 4,
      }}
    >
      {summaryData.map((summary, index) => (
        <Grid
          item
          xs={12}
          sm={6}
          md={2.4}
          key={index}
          sx={{
            borderRight: {
              md:
                index !== summaryData.length - 1
                  ? `1px solid rgba(35, 6, 3, 0.07)`
                  : 'none',
            },
          }}
        >
          {renderSummary(summary, { ...summary?.sx })}
        </Grid>
      ))}
    </Grid>
  );
};
